/* eslint-disable @typescript-eslint/no-explicit-any */
import { Autocomplete, Stack, SxProps, TextField, Theme } from '@mui/material';
import { fieldStyles, selectMenuProps } from 'components/styles/common';
import { ClearFiltersButton } from 'components/ui/buttons/ClearFiltersButton';
import { useEffect, useRef, useState } from 'react';
import { UniversalDatePicker } from '../../data-grid/components/date-picker/UniversalDatePicker';
import { UniversalDateRangePicker } from '../../data-grid/components/date-picker/UniversalDateRangePicker';
import { TableToolbarFilter } from '../../types/toolbar.types';

interface TableFilterSectionProps {
  /** Dynamic array containing data filter configurations */
  filters: TableToolbarFilter[];
  /** System style override object leveraging Material-UI theme layout engines */
  sx?: SxProps<Theme>;
  /** Callback emitted when state updates, useful to trigger grid/API reloads */
  onFiltersChange?: (activeFilters: Record<string, any>) => void;
  /** Controls visibility of the mass clearance controller */
  showClearAll?: boolean;
}

export const TableFilterSection = ({
  filters,
  sx,
  onFiltersChange,
  showClearAll = true
}: TableFilterSectionProps) => {
  const previousPayloadRef = useRef<string>('');

  const getInitialValuesFromURL = (): Record<string, any> => {
    const params = new URLSearchParams(window.location.search);
    const initial: Record<string, any> = {};

    filters.forEach((f) => {
      if (f.type === 'dateRange') {
        const start = params.get(`${f.id}_start`);
        const end = params.get(`${f.id}_end`);
        initial[f.id] = [start || null, end || null];
      } else {
        const val = params.get(f.id);
        initial[f.id] = val !== null ? val : '';
      }
    });
    return initial;
  };

  const [filterValues, setFilterValues] = useState<Record<string, any>>(getInitialValuesFromURL);

  console.log(filterValues);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    Object.entries(filterValues).forEach(([id, value]) => {
      const config = filters.find((f) => f.id === id);

      if (config?.type === 'dateRange' && Array.isArray(value)) {
        const [start, end] = value;
        if (start) params.set(`${id}_start`, start);
        else params.delete(`${id}_start`);
        if (end) params.set(`${id}_end`, end);
        else params.delete(`${id}_end`);
      } else if (value !== undefined && value !== null && value !== '') {
        params.set(id, String(value));
      } else {
        params.delete(id);
      }
    });

    const newRelativePathQuery = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState(null, '', newRelativePathQuery);

    if (onFiltersChange) {
      const currentPayloadString = JSON.stringify(filterValues);
      if (previousPayloadRef.current !== currentPayloadString) {
        previousPayloadRef.current = currentPayloadString;
        onFiltersChange(filterValues);
      }
    }
  }, [filterValues, filters, onFiltersChange]);

  const handleFilterChange = (id: string, newValue: any) => {
    let processedValue = newValue;

    if (typeof newValue === 'string') {
      processedValue =
        newValue.trim() !== '' && !isNaN(Number(newValue)) ? Number(newValue) : newValue;
    }

    setFilterValues((prev) => ({ ...prev, [id]: processedValue }));
  };

  const handleClearAll = () => {
    const resetValues: Record<string, any> = {};
    filters.forEach((f) => {
      resetValues[f.id] = f.type === 'dateRange' ? [null, null] : '';
    });
    setFilterValues(resetValues);
  };

  const hasActiveFilters = filters.some((filter) => {
    const value = filterValues[filter.id];
    if (!value) return false;
    if (filter.type === 'dateRange' && Array.isArray(value)) {
      return value.some((date) => date !== null && date !== '');
    }
    return value !== '';
  });

  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ ...sx }}>
      {filters.map((filter) => {
        const currentValue = filterValues[filter.id];

        if (filter.type === 'select') {
          const currentOption =
            filter.options?.find((opt) => {
              return String(opt.value) === String(currentValue);
            }) || (currentValue ? { label: 'Caricamento...', value: currentValue } : null);

          return (
            <Autocomplete
              key={filter.id}
              size="small"
              options={filter.options || []}
              getOptionLabel={(option) => (option ? String(option.label) : '')}
              value={currentOption}
              onChange={(_, newValue) => {
                handleFilterChange(filter.id, newValue ? newValue.value : '');
              }}
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                return String(option.value) === String(value.value);
              }}
              disablePortal={selectMenuProps.disablePortal}
              slotProps={selectMenuProps.slotProps}
              sx={{
                ...fieldStyles(filter.minWidth),
                maxWidth: 200,
                '& .MuiAutocomplete-input': {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={filter.label}
                  placeholder={!currentOption ? filter.label || 'Cerca...' : ''}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    shrink: true
                  }}
                />
              )}
            />
          );
        }

        if (filter.type === 'date') {
          return (
            <UniversalDatePicker
              key={filter.id}
              name={filter.id}
              placeholder={filter.label}
              value={currentValue || null}
              onChange={(newValue) => handleFilterChange(filter.id, newValue)}
              noFuture={!!filter.noFuture}
              disabled={!!filter.disabled}
              disableOpenPicker={!!filter.disableOpenPicker}
              minWidth={filter.minWidth || 200}
              error={!!filter.error}
              helperText={filter.error || ''}
            />
          );
        }

        if (filter.type === 'dateRange') {
          const resolvedRange: [string | null, string | null] =
            Array.isArray(currentValue) && currentValue.length === 2
              ? [currentValue[0] || null, currentValue[1] || null]
              : [null, null];
          return (
            <UniversalDateRangePicker
              key={filter.id}
              value={resolvedRange}
              onChange={(newPayload) => {
                if (newPayload) {
                  handleFilterChange(filter.id, newPayload);
                } else {
                  handleFilterChange(filter.id, [null, null]);
                }
              }}
              placeholder={filter.label}
              disabled={!!filter.disabled}
              noFuture={!!filter.noFuture}
              disableOpenPicker={!!filter.disableOpenPicker}
              error={!!filter.error}
              helperText={filter.error || ''}
              minWidth={260}
            />
          );
        }

        // Default Text Input
        return (
          <TextField
            key={filter.id}
            size="small"
            placeholder={filter.label}
            value={currentValue || ''}
            onChange={(e) => handleFilterChange(filter.id, e.target.value)}
            sx={fieldStyles(filter.minWidth)}
          />
        );
      })}

      {showClearAll && hasActiveFilters && <ClearFiltersButton onClear={handleClearAll} />}
    </Stack>
  );
};
